import type { MouseEvent } from 'react';
import { useApp } from '../store';
import { sampleReadOnlyNote } from '../sample-note';
import Icon from './Icon';

/**
 * THE READ-ONLY SAMPLE TREATMENT (Vinnie-approved mock 03).
 *
 * A sample board is a worked example anyone can open to see how a board is set
 * up — so it is READ-ONLY, but calm, not walled: it states what it is once at
 * the top, keeps a way out always in reach, and if someone tries to change
 * something they get a gentle note, never a dead click (mock 03).
 *
 * This file is the shared machinery: the strip itself, `useSampleBoard` (what a
 * sample board is), and `useSampleInert` (the props a write control spreads to
 * render itself inert). Read-only is enforced in the store — every mutating
 * action refuses the write and shows the note on a sample board (sampleBlocks /
 * sampleNoWrite), while the viewer KEEPS their real role so nav and read-only
 * screens are unchanged (recomputeMyRole does not lower the role). This file is
 * the VISIBLE treatment the mock asks for on top of that safety.
 */

const BLURB: Record<'project' | 'service', string> = {
  project: 'A finished delivery project, set up the way a good team would. Explore freely — nothing here can be changed.',
  service: 'An ongoing service board, run the way a good team would. Explore freely — nothing here can be changed.',
};

/** What a sample board is and does, for the strip and for every inert control. */
export function useSampleBoard() {
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const setProject = useApp((s) => s.setProject);
  const setStarting = useApp((s) => s.setStartingBoard);

  const cur = projects.find((p) => p.id === projectId);
  const readOnly = !!cur?.sample;
  const name = cur?.name ?? '';
  const kind: 'project' | 'service' = cur?.deliveryMode === 'continuous' ? 'service' : 'project';

  // Leave for a real board of your own — the one whose work actually matters.
  // If there is none yet, the way out is to start one.
  const leave = () => {
    const real = projects.find((p) => !p.sample && !p.archived && p.id !== projectId);
    if (real) setProject(real.id);
    else setStarting(true);
  };

  // The gentle note when a change is attempted — the shared one (sample-note.ts),
  // so a control's click and the store's write guard say exactly the same thing.
  const note = async () => {
    if (await sampleReadOnlyNote(name)) setStarting(true);
  };

  return {
    readOnly,
    name,
    kind,
    /** The one-line reason an inert control shows on hover. "Make changes" (not
     *  "add work"): the same string sits on resolve/assign/move controls too, so
     *  it must be true for any write, not just creating a card (copy-witness F2). */
    reason: 'Read-only sample — start a board of your own to make changes.',
    leave,
    note,
  };
}

/**
 * The props to spread onto a WRITE control so it reads as inert on a sample
 * board (mock 03): present and visibly disabled (`is-inert`), a one-line reason
 * on hover, and a click that answers with the gentle note instead of a dead
 * press. Off a sample board it returns the control's own className and handler
 * unchanged, so a real board behaves exactly as before.
 *
 *   const inert = useSampleInert();
 *   <button {...inert('btn btn-primary', () => startCreate(), 'New issue')}>…</button>
 *
 * Put this ONLY on a control that writes (create / edit / move / delete / post /
 * sign / upload). Never on a read / open / nav control — reading a sample stays
 * fully open (mock 03, principle 4). The spread must come after `type` etc. and
 * replaces the control's own className + onClick + title.
 */
export function useSampleInert() {
  const { readOnly, reason, note } = useSampleBoard();
  return function inert(
    className: string,
    onClick: (e: MouseEvent<HTMLElement>) => void,
    title?: string,
  ): {
    className: string;
    onClick: (e: MouseEvent<HTMLElement>) => void;
    title?: string;
    'aria-disabled'?: true;
  } {
    if (!readOnly) return { className, onClick, title };
    return {
      className: `${className} is-inert`,
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        void note();
      },
      title: reason,
      'aria-disabled': true,
    };
  };
}

/** The strip across the top of a sample board — states it once, way out in reach. */
export default function SampleStrip() {
  const { readOnly, name, kind, leave } = useSampleBoard();
  if (!readOnly) return null;
  return (
    <div className="sample-strip" role="note">
      <span className="sample-eye">
        <Icon name="eye" size={16} />
      </span>
      <span className="sample-label">Sample {kind === 'service' ? 'service board' : 'project'} · read only</span>
      <span className="sample-words">
        <b>{name}.</b> {BLURB[kind]}
      </span>
      <button className="sample-leave" onClick={leave}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 6l-6 6 6 6" />
        </svg>
        Back to your boards
      </button>
    </div>
  );
}
